import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { Button } from 'reactstrap';
import Icon from '../common/containers/icon';
import getPageInfo from './getPageInfo';
import FavoriteTitlePromptModal from './favoriteTitlePromptModal';
import { addFavoriteWithValidation, FAVORITES_UNAVAILABLE_USER_MESSAGE, FavoritesUnavailableError, findFavoriteByPageUrl, getPinnedFavoritesFromPreferencesContent, isPageUrlFavorited, prepareAddFavorite } from './favoritesHelpers';
import { getError } from '../../actions/ajax';
import entityComposition from '../common/classes/entityComposition';
import { getPreferencesContent, useUserPreferencesFavorites } from './userPreferencesFavorites';
const FavoriteButton = ({
  notificationActions: _notificationActions = {},
  form: _form = {},
  size: _size = 'sm'
}) => {
  var _form$active;
  const location = useLocation();
  const {
    preferencesContent,
    loading,
    ensureLoaded,
    upsertPinnedFavorite,
    removePinnedFavorite
  } = useUserPreferencesFavorites();
  const formTitle = (_form === null || _form === void 0 || (_form$active = _form.active) === null || _form$active === void 0 || (_form$active = _form$active.content) === null || _form$active === void 0 ? void 0 : _form$active.title) || null;
  const [showTitlePrompt, setShowTitlePrompt] = useState(false);
  const [customTitle, setCustomTitle] = useState('');
  const [closeModalSignal, setCloseModalSignal] = useState(0);
  const titleInputRef = useRef(null);
  const currentUrl = `${location.pathname}${location.search}`;
  const getCurrentPageInfo = useCallback(() => getPageInfo(formTitle || null), [formTitle]);
  const favorites = getPinnedFavoritesFromPreferencesContent(preferencesContent);
  const isFavorited = !loading && isPageUrlFavorited(favorites, currentUrl);
  const persistPinnedFavorite = useCallback(favorite => upsertPinnedFavorite(favorite), [upsertPinnedFavorite]);
  const markFavoriteAsRemoved = useCallback(async pinId => removePinnedFavorite(pinId), [removePinnedFavorite]);
  useEffect(() => {
    ensureLoaded().catch(() => {});
  }, [ensureLoaded, location.pathname, location.search]);
  useEffect(() => {
    if (showTitlePrompt && titleInputRef.current) {
      setTimeout(() => {
        var _titleInputRef$curren;
        (_titleInputRef$curren = titleInputRef.current) === null || _titleInputRef$curren === void 0 || _titleInputRef$curren.focus();
      }, 100);
    }
  }, [showTitlePrompt]);
  useEffect(() => {
    if (closeModalSignal === 0) {
      return;
    }
    setShowTitlePrompt(false);
    setCustomTitle('');
    setCloseModalSignal(0);
  }, [closeModalSignal]);
  const handleTitleCancel = useCallback(() => {
    setShowTitlePrompt(false);
    setCustomTitle('');
  }, []);
  const handleTitleSubmit = useCallback(async () => {
    const trimmedTitle = customTitle.trim();
    if (!trimmedTitle) {
      return;
    }
    try {
      const added = await addFavoriteWithValidation({
        persistPinnedFavorite,
        getPageInfo: getCurrentPageInfo,
        title: trimmedTitle,
        form: _form,
        notificationActions: _notificationActions
      });
      if (added === null) {
        return;
      }
      setShowTitlePrompt(false);
      setCustomTitle('');
      setCloseModalSignal(s => s + 1);
    } catch (err) {
      const {
        error_msg: errorMsg
      } = getError(err);
      if (_notificationActions !== null && _notificationActions !== void 0 && _notificationActions.error) {
        _notificationActions.error({
          headerMessage: 'Failed to add favorite',
          message: errorMsg || 'Please try again.',
          id: 'favorite-add-failed'
        });
      }
    }
  }, [customTitle, persistPinnedFavorite, getCurrentPageInfo, _form, _notificationActions]);
  const handleToggleFavorite = async () => {
    await ensureLoaded().catch(() => {});
    const loadedFavorites = getPinnedFavoritesFromPreferencesContent(getPreferencesContent());
    const pageUrl = `${location.pathname}${location.search}`;
    const isFavoritedNow = isPageUrlFavorited(loadedFavorites, pageUrl);
    if (isFavoritedNow) {
      const favoriteToRemove = findFavoriteByPageUrl(loadedFavorites, pageUrl);
      if (favoriteToRemove) {
        try {
          const removed = await markFavoriteAsRemoved(favoriteToRemove.id);
          if (!removed) {
            return;
          }
          if (_notificationActions !== null && _notificationActions !== void 0 && _notificationActions.info) {
            const currentPageInfo = getCurrentPageInfo();
            _notificationActions.info({
              headerMessage: 'Favorite Removed',
              message: `"${favoriteToRemove.label || currentPageInfo.title}" has been removed from your favorites.`,
              id: 'favorite-removed'
            });
          }
        } catch (err) {
          if (err instanceof FavoritesUnavailableError) {
            if (_notificationActions !== null && _notificationActions !== void 0 && _notificationActions.error) {
              _notificationActions.error({
                headerMessage: 'Failed to remove favorite',
                message: FAVORITES_UNAVAILABLE_USER_MESSAGE,
                id: 'favorite-remove-unavailable'
              });
            }
            return;
          }
          const {
            error_msg: errorMsg
          } = getError(err);
          if (_notificationActions !== null && _notificationActions !== void 0 && _notificationActions.error) {
            _notificationActions.error({
              headerMessage: 'Failed to remove favorite',
              message: errorMsg || 'Please try again.',
              id: 'favorite-remove-failed'
            });
          }
        }
      }
      return;
    }
    const {
      canProceed,
      defaultTitle
    } = prepareAddFavorite({
      getPageInfo: getCurrentPageInfo,
      form: _form,
      notificationActions: _notificationActions
    });
    if (!canProceed) {
      return;
    }
    setCustomTitle(defaultTitle);
    setShowTitlePrompt(true);
  };
  let starTitle = 'Add to favorites';
  if (loading) {
    starTitle = 'Loading favorites';
  } else if (isFavorited) {
    starTitle = 'Remove from favorites';
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
    type: "button",
    size: _size,
    onClick: handleToggleFavorite,
    "data-testid": "t-button-favorites",
    title: starTitle
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "star",
    weight: isFavorited ? 'fas' : 'far'
  })), /*#__PURE__*/React.createElement(FavoriteTitlePromptModal, {
    isOpen: showTitlePrompt,
    title: customTitle,
    onTitleChange: setCustomTitle,
    onSubmit: handleTitleSubmit,
    onCancel: handleTitleCancel,
    inputRef: titleInputRef
  }));
};
FavoriteButton.propTypes = {
  notificationActions: PropTypes.shape({
    info: PropTypes.func,
    error: PropTypes.func
  }),
  form: PropTypes.shape({
    active: PropTypes.shape({
      content: PropTypes.shape({
        title: PropTypes.string
      })
    }),
    pendingChanges: PropTypes.bool
  }),
  size: PropTypes.string
};
export { FavoriteButton as UncomposedFavoriteButton };
export default entityComposition(FavoriteButton, ['form'], {}, ['notification']);